'use client'

import { useRef, useEffect, useState, useMemo } from 'react'
import { PlayerInterface } from '@foundation-player/api'
import { Icon } from '@/components/Icon'
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer'
import { type WidgetVideoProps } from '@/types/cms'
import StructuredData from '@/elements/widgets/Video/StructuredData'
import { createPlayerProps, useTracking, useSticky } from './utils'
import styles from './VideoInlineTop.module.scss'
import AppContext from '@/types/context'
import { useIsPurMode } from '@/hooks/useIsPurMode'

const WidgetVideoInlineTop = ({ item, context }: { item: WidgetVideoProps; context: AppContext }) => {
  const playerWrapper = useRef<HTMLDivElement>(null)
  const [player, setPlayer] = useState<PlayerInterface | undefined>(undefined)
  const [stickyState, setStickyState] = useSticky({
    playerWrapper: playerWrapper,
    player: player,
    isInlineTop: true,
  })

  const isPurMode = useIsPurMode()

  const videoContext = item.items[0]
  const videoPlayer = useMemo(
    () => (
      <VideoPlayer
        props={createPlayerProps({
          context: videoContext,
          autoplay: false,
          heartbeatStarttype: 0,
          isPurMode,
        })}
        providePlayer={setPlayer}
      />
    ),
    [videoContext],
  )

  useTracking(stickyState, 'stickyVideoArticle', videoContext.model.id)

  useEffect(() => {
    if (player) {
      player.state.onValueChange('sessionCompleted', ({ sessionCompleted }: { sessionCompleted: boolean }) => {
        if (sessionCompleted) setStickyState({ isSticky: false, trigger: 'sessionEnd' })
      })
    }
  }, [player])

  return (
    <>
      <StructuredData item={videoContext} />
      <div className={styles.video}>
        <div className={styles.player} key={videoContext.model.id}>
          <div
            ref={playerWrapper}
            className={stickyState.isSticky ? styles.sticky + ' ' + styles['sticky-article'] : undefined}
          >
            <div>
              {videoPlayer}
              {stickyState.isSticky && (
                <button
                  className={styles.close}
                  onClick={() => {
                    player?.pause()
                    setStickyState({ isSticky: false, trigger: 'close' })
                  }}
                >
                  <Icon name="close" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default WidgetVideoInlineTop
